import React, {useEffect, useRef, useState} from "react";

/**
 * HexPCMPlayer
 * ------------
 * React component that converts raw PCM audio provided as a hexadecimal string
 * into a playable <audio> element by dynamically prepending a WAV header.
 *
 * Why prepend a WAV header?
 * ------------------------
 * Browsers' <audio> tag understands container formats (WAV, MP3, Ogg, …).
 * Pure PCM has **no** header/metadata, so we generate a minimal 44‑byte WAV
 * header on the fly, copy‑concat the PCM payload, and hand it to the browser
 * as `audio/wav`.
 *
 * Props
 * -----
 * hexData        (string, required) – Continuous hex string containing the **PCM payload only**.
 * sampleRate     (number, default 44100)
 * numChannels    (number, default 1) – 1 = mono, 2 = stereo.
 * bitsPerSample  (number, default 16) – Typical values: 8, 16, 24, 32.
 * autoPlay       (boolean, default false)
 *
 * Example
 * -------
 * ```jsx
 * <HexPCMPlayer
 *   hexData={pcmHex}
 *   sampleRate={16000}
 *   numChannels={1}
 *   bitsPerSample={16}
 *   autoPlay
 * />
 * ```
 */
export default function HexPCMPlayer({
                                         hexData,
                                         onLoad,
                                         onEnded,
                                         sampleRate = 44100,
                                         numChannels = 1,
                                         bitsPerSample = 16,
                                         autoPlay = false,
                                     }) {
    const audioRef = useRef();
    const [audioUrl, setAudioUrl] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!audioRef.current || !audioUrl) return;
        console.log("Setting audio source to:", audioUrl);

        audioRef.current.src = audioUrl;
        audioRef.current.load();
        if (autoPlay) {
            audioRef.current.play().catch(err => {
                console.error("Audio playback failed:", err);
            });
        }
    }, [audioRef, audioUrl]);

    /**
     * Convert a hex string (with possible whitespace) to Uint8Array
     */
    const hexToBytes = (hex) => {
        const clean = hex.replace(/[^0-9a-fA-F]/g, "");
        if (clean.length % 2 !== 0) throw new Error("Hex string must have even length");
        const bytes = new Uint8Array(clean.length / 2);
        for (let i = 0; i < clean.length; i += 2) {
            bytes[i / 2] = parseInt(clean.slice(i, i + 2), 16);
        }
        return bytes;
    };

    /**
     * Build a 44‑byte WAV header for the given PCM parameters.
     */
    const buildWavHeader = ({dataLength}) => {
        const blockAlign = (numChannels * bitsPerSample) / 8;
        const byteRate = sampleRate * blockAlign;
        const buffer = new ArrayBuffer(44);
        const view = new DataView(buffer);

        let offset = 0;
        // "RIFF"
        view.setUint32(offset, 0x52494646, false); // "RIFF" big‑endian
        offset += 4;
        view.setUint32(offset, 36 + dataLength, true); // file size minus 8
        offset += 4;
        view.setUint32(offset, 0x57415645, false); // "WAVE"
        offset += 4;

        // fmt  chunk
        view.setUint32(offset, 0x666d7420, false); // "fmt "
        offset += 4;
        view.setUint32(offset, 16, true); // PCM header size
        offset += 4;
        view.setUint16(offset, 1, true); // audio format 1 = PCM
        offset += 2;
        view.setUint16(offset, numChannels, true);
        offset += 2;
        view.setUint32(offset, sampleRate, true);
        offset += 4;
        view.setUint32(offset, byteRate, true);
        offset += 4;
        view.setUint16(offset, blockAlign, true);
        offset += 2;
        view.setUint16(offset, bitsPerSample, true);
        offset += 2;

        // data chunk
        view.setUint32(offset, 0x64617461, false); // "data"
        offset += 4;
        view.setUint32(offset, dataLength, true);

        return new Uint8Array(buffer);
    };

    useEffect(() => {
        if (!hexData) return;
        try {
            // Step 1: Convert hex to raw PCM bytes
            const pcmBytes = hexToBytes(hexData);
            // Step 2: Generate WAV header
            const headerBytes = buildWavHeader({dataLength: pcmBytes.length});
            // Step 3: Concatenate header + pcm
            const wavBytes = new Uint8Array(headerBytes.length + pcmBytes.length);
            wavBytes.set(headerBytes, 0);
            wavBytes.set(pcmBytes, headerBytes.length);
            // Step 4: Create Blob & ObjectURL
            const blob = new Blob([wavBytes.buffer], {type: "audio/wav"});
            const url = URL.createObjectURL(blob);
            setAudioUrl(url);
            return () => URL.revokeObjectURL(url);
        } catch (e) {
            setError(e.message);
        }
    }, [hexData, sampleRate, numChannels, bitsPerSample]);

    if (error) {
        return (
            <div className="p-4 bg-red-100 text-red-800 rounded-xl">
                PCM Error: {error}
            </div>
        );
    }

    if (!audioUrl) {
        return (
            <div style={{display: "none"}}/>
        );
    }

    return (
        <audio
            ref={audioRef}
            onCanPlay={onLoad}
            onEnded={onEnded}
        />
    );
}
